import React from 'react';
import { Target, Globe, CheckCircle, GraduationCap, BarChart3, MapPin } from 'lucide-react';
import Lab from '../images/AI-Kit.jpeg';

const AITrainerKitResearch = () => {
  const features = [
    { icon: <Target size={24} />, title: 'All-in-One AI Learning Solution', description: 'Combines AI, ML, Computer Vision, NLP, Robotics, and IoT in a single, portable device—no need for multiple tools or complex setups.', color: '#6366F1' },
    { icon: <Globe size={24} />, title: 'Intel-Powered, Globally Compatible', description: 'Powered by Intel technology and aligned with NEP 2020, CBSE (USA), UNESCO, ISA, and 21st Century Skills frameworks.', color: '#10B981' },
    { icon: <CheckCircle size={24} />, title: 'Truly Portable & Plug-and-Play', description: 'Lightweight, compact, and easy to set up anywhere—perfect for classrooms, rural outreach, workshops, or innovation fairs.', color: '#F59E0B' },
    { icon: <GraduationCap size={24} />, title: 'Beginner-Friendly, Scalable Learning', description: 'No coding experience needed to start. Scales from middle school to university level, supporting both foundational and advanced learning.', color: '#8B5CF6' },
    { icon: <BarChart3 size={24} />, title: 'Internship & Industry Exposure', description: 'Offers eligible students opportunities for internships at partner companies, along with industry visits and exposure trips globally.', color: '#6366F1' }
  ];

  const stats = [
    { value: '10x', label: 'More Affordable', subtitle: 'Than traditional AI labs' },
    { value: '100%', label: 'Plug & Play', subtitle: 'No complex setup required' }
  ];

  // Base styles object
  const styles = {
    container: {
      backgroundColor: '#0a0a0a',
      minHeight: '100vh',
      padding: '80px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    contentWrapper: {
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '80px'
    },
    title: {
      color: 'white',
      fontSize: '48px',
      fontWeight: '800',
      marginBottom: '24px',
      letterSpacing: '-0.02em'
    },
    subtitle: {
      color: '#94A3B8',
      fontSize: '18px',
      lineHeight: '1.6',
      maxWidth: '800px',
      margin: '0 auto'
    },
    mainGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '80px',
      alignItems: 'start'
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '40px'
    },
    image: {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '16px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
    },
    whyChooseCard: {
      backgroundColor: '#0a0a0a',
      border: '1px solid #334155',
      borderRadius: '16px',
      padding: '32px'
    },
    whyChooseHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '24px'
    },
    whyChooseIconContainer: {
      backgroundColor: '#0a0a0a',
      borderRadius: '12px',
      width: '48px',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    whyChooseTitle: {
      color: 'white',
      fontSize: '24px',
      fontWeight: '700',
      margin: 0
    },
    whyChooseDescription: {
      color: '#94A3B8',
      fontSize: '16px',
      lineHeight: '1.6',
      margin: 0
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '24px'
    },
    statCard: {
      backgroundColor: '#0a0a0a',
      border: '1px solid #334155',
      borderRadius: '12px',
      padding: '24px',
      textAlign: 'center'
    },
    statValue: {
      color: '#6366F1',
      fontSize: '36px',
      fontWeight: '800',
      marginBottom: '8px'
    },
    statLabel: {
      color: 'white',
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: '4px'
    },
    statSubtitle: {
      color: '#94A3B8',
      fontSize: '14px'
    },
    rightColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    featureCard: {
      backgroundColor: '#0a0a0a',
      border: '1px solid #334155',
      borderRadius: '16px',
      padding: '24px',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    featureContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px'
    },
    featureIconContainer: (color) => ({
      backgroundColor: color,
      borderRadius: '12px',
      width: '48px',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }),
    featureTitle: {
      color: 'white',
      fontSize: '20px',
      fontWeight: '600',
      margin: '0 0 12px 0'
    },
    featureDescription: {
      color: '#94A3B8',
      fontSize: '16px',
      lineHeight: '1.6',
      margin: 0
    },
    ctaContainer: {
      textAlign: 'center',
      marginTop: '80px'
    },
    ctaButton: {
      background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '16px 32px',
      fontSize: '18px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)'
    }
  };

  return (
    <div style={styles.container} data-component="container">
      <style>
        {`
          /* --- Hover Effects --- */
          [data-component="feature-card"]:hover {
            transform: translateY(-2px);
            border-color: var(--hover-color);
            /* color-mix is a modern CSS function to create rgba from a variable */
            box-shadow: 0 8px 32px color-mix(in srgb, var(--hover-color) 20%, transparent);
          }
          [data-component="cta-button"]:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 32px rgba(99, 102, 241, 0.4);
          }
          
          /* --- Responsive Design --- */
          @media (max-width: 992px) {
            [data-component="main-grid"] {
              grid-template-columns: 1fr !important;
              gap: 60px !important;
            }
          }
          
          @media (max-width: 768px) {
            [data-component="container"] {
              padding: 60px 15px !important;
            }
            [data-component="header"] {
              margin-bottom: 60px !important;
            }
            [data-component="title"] {
              font-size: 38px !important;
            }
            [data-component="subtitle"] {
              font-size: 16px !important;
            }
            [data-component="stats-grid"] {
              grid-template-columns: 1fr !important;
            }
            [data-component="cta-container"] {
              margin-top: 60px !important;
            }
          }
        `}
      </style>
      <div style={styles.contentWrapper}>
        <div style={styles.header} data-component="header">
          <h1 style={styles.title} data-component="title">Research Using AI Trainer Kit</h1>
          <p style={styles.subtitle} data-component="subtitle">
            Revolutionary all-in-one AI learning solution combining cutting-edge technology with accessibility and affordability for educational institutions worldwide.
          </p>
        </div>

        <div style={styles.mainGrid} data-component="main-grid">
          <div style={styles.leftColumn}>
            <div>
              <img src={Lab} alt="AI Trainer Kit" style={styles.image} />
            </div>
            <div style={styles.whyChooseCard}>
              <div style={styles.whyChooseHeader}>
                <div style={styles.whyChooseIconContainer}>
                  <Target size={24} color="white" />
                </div>
                <h3 style={styles.whyChooseTitle}>Why Choose the AI Trainer Kit?</h3>
              </div>
              <p style={styles.whyChooseDescription}>
                The ultimate portable AI education solution that brings comprehensive AI, ML, Computer Vision, NLP, Robotics, and IoT learning capabilities to any classroom, anywhere in the world.
              </p>
            </div>
            <div style={styles.statsGrid} data-component="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} style={styles.statCard}>
                  <div style={styles.statValue}>{stat.value}</div>
                  <div style={styles.statLabel}>{stat.label}</div>
                  <div style={styles.statSubtitle}>{stat.subtitle}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.rightColumn}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={{ ...styles.featureCard, '--hover-color': feature.color }}
                data-component="feature-card"
              >
                <div style={styles.featureContent}>
                  <div style={styles.featureIconContainer(feature.color)}>
                    {React.cloneElement(feature.icon, { color: 'white' })}
                  </div>
                  <div>
                    <h4 style={styles.featureTitle}>{feature.title}</h4>
                    <p style={styles.featureDescription}>{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.ctaContainer} data-component="cta-container">
          <a href="/ai-trainer-kit">
            <button style={styles.ctaButton} data-component="cta-button">
              <MapPin size={20} />
              Learn More About AI Trainer Kit
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AITrainerKitResearch;